import { atom } from "jotai";
import type { Edge, Node } from "@xyflow/react";
import { v4 as uuidv4 } from "uuid";
import type { NoxusReactflowNodeV2 } from "@/types/spotflow-v2";
import type { NodeDefinition } from "@/generated";
import { SpotToast } from "@/components/SpotToast";
import { applyDagreLayout } from "@/components/Spotflow/Editor/layout/dagre-layout";
import { GROUP_PADDING, getBoundingBox } from "@/utils/group-utils";
import {
  nodesAtom,
  edgesAtom,
  dragOverContainerIdAtom,
  isEditorReadOnlyAtom,
  nodeRegistryAtom,
} from "../atoms";
import {
  createGroupReactFlowNode,
  findNodeDisplayName,
  markDirty,
  type DirtyOrigin,
} from "../helpers";
import {
  absoluteNodePosition,
  applyGroupFrame,
  expandGroupFrameToContain,
  groupContainingPoint,
  groupFrame,
  isGroupReactFlowNode,
  nodeSize,
} from "../groups";
import { recordTransaction } from "../history/recordTransaction";

// ── Reparent on drag-stop (group containment) ─────────────────────────────

/** The group frame under the dragged node's centre. */
function groupUnderNode(
  node: NoxusReactflowNodeV2,
  nodes: NoxusReactflowNodeV2[],
  byId: Map<string, NoxusReactflowNodeV2>
): NoxusReactflowNodeV2 | undefined {
  const abs = absoluteNodePosition(node, byId);
  const size = nodeSize(node);
  return groupContainingPoint(nodes, {
    x: abs.x + size.width / 2,
    y: abs.y + size.height / 2,
  });
}

/** Groups never nest, and a group's children are pinned to it — they leave
 *  through "Remove from group", not by dragging. */
function isDragReparentable(
  node: NoxusReactflowNodeV2,
  byId: Map<string, NoxusReactflowNodeV2>
): boolean {
  if (isGroupReactFlowNode(node)) return false;
  const parent = node.parentId ? byId.get(node.parentId) : undefined;
  return !isGroupReactFlowNode(parent);
}

/**
 * Called from `onNodeDrag`. Points `dragOverContainerIdAtom` at the group
 * the dragged node's centre is over (or null). The group reads it and
 * highlights itself as a valid drop target.
 *
 * Throttled implicitly by React Flow's drag rate; cheap enough to recompute.
 */
export const updateDragOverContainerAtom = atom(
  null,
  (get, set, nodeId: string) => {
    const clear = () => {
      if (get(dragOverContainerIdAtom) !== null) {
        set(dragOverContainerIdAtom, null);
      }
    };
    if (get(isEditorReadOnlyAtom)) {
      clear();
      return;
    }
    const nodes = get(nodesAtom);
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const node = byId.get(nodeId);
    if (!node || !isDragReparentable(node, byId)) {
      clear();
      return;
    }
    const targetId = groupUnderNode(node, nodes, byId)?.id ?? null;
    if (get(dragOverContainerIdAtom) !== targetId) {
      set(dragOverContainerIdAtom, targetId);
    }
  }
);

/**
 * Called from `onNodeDragStop` / `onSelectionDragStop` with every node that
 * moved. Each node is judged against the frames as they were before the drop,
 * so two nodes dropped together both join by where they landed, not by a
 * frame the first one already grew. Joining is one history entry: one undo
 * pops the nodes back out where they were dropped, the next undoes the move.
 */
export const reparentNodesOnDropAtom = atom(
  null,
  (get, set, nodeIds: ReadonlyArray<string>) => {
    set(dragOverContainerIdAtom, null);
    if (get(isEditorReadOnlyAtom)) return;

    const nodes = get(nodesAtom);
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const joins = nodeIds.flatMap((id) => {
      const node = byId.get(id);
      if (!node || !isDragReparentable(node, byId)) return [];
      const group = groupUnderNode(node, nodes, byId);
      return group ? [{ node, group }] : [];
    });
    if (joins.length === 0) return;

    let next = nodes;
    for (const { node, group } of joins) {
      const abs = absoluteNodePosition(node, byId);
      const liveGroup = next.find((n) => n.id === group.id) ?? group;
      const current = groupFrame(liveGroup);
      const frame =
        expandGroupFrameToContain(current, { ...abs, ...nodeSize(node) }) ??
        current;
      next = applyGroupFrame(next, group.id, frame).map((n) =>
        n.id === node.id
          ? {
              ...n,
              parentId: group.id,
              extent: "parent" as const,
              position: {
                x: abs.x - frame.position.x,
                y: abs.y - frame.position.y,
              },
            }
          : n
      );
    }
    // React Flow requires the parent to come before its children in the array.
    const joinedIds = new Set(joins.map((j) => j.node.id));
    const ordered = [
      ...next.filter((n) => !joinedIds.has(n.id)),
      ...next.filter((n) => joinedIds.has(n.id)),
    ];

    const groupIds = new Set(joins.map((j) => j.group.id));
    const target =
      groupIds.size === 1
        ? findNodeDisplayName(nodes, joins[0].group.id)
        : "groups";
    const subject =
      joins.length === 1
        ? findNodeDisplayName(nodes, joins[0].node.id)
        : `${joins.length} nodes`;
    recordTransaction(
      get,
      set,
      `Move ${subject} into ${target}`,
      ({ nodes: draft }) => {
        draft.length = 0;
        for (const n of ordered) {
          draft.push(n as unknown as (typeof draft)[number]);
        }
      }
    );
    markDirty(set);
  }
);

// ── Group selected nodes ──────────────────────────────────────────────────

export const groupSelectedNodesV2Atom = atom(null, (get, set) => {
  if (get(isEditorReadOnlyAtom)) return;
  const nodes = get(nodesAtom);

  // Selected top-level nodes that are not group containers themselves
  const selectedTopLevel = nodes.filter(
    (n) =>
      n.selected &&
      (n as { type: string }).type !== "groupNodeV2" &&
      !n.parentId
  );

  // Separately selected groups to dissolve
  const selectedGroups = nodes.filter(
    (n) => n.selected && n.type === "groupNodeV2"
  );

  const dissolvedGroupIds = new Set(selectedGroups.map((g) => g.id));

  // Children of dissolved groups — convert to absolute positions
  const childrenOfDissolved = nodes
    .filter((n) => n.parentId && dissolvedGroupIds.has(n.parentId))
    .map((n) => {
      const parentGroup = selectedGroups.find((g) => g.id === n.parentId);
      return {
        ...n,
        parentId: undefined,
        extent: undefined as "parent" | undefined,
        position: {
          x: n.position.x + (parentGroup?.position.x ?? 0),
          y: n.position.y + (parentGroup?.position.y ?? 0),
        },
      };
    });

  const nodesToGroup = [...selectedTopLevel, ...childrenOfDissolved];
  if (nodesToGroup.length < 2) return;

  const { minX, minY, maxX, maxY } = getBoundingBox(
    nodesToGroup as unknown as Parameters<typeof getBoundingBox>[0]
  );

  const groupX = minX - GROUP_PADDING;
  const groupY = minY - GROUP_PADDING - 20;
  const groupWidth = maxX - minX + GROUP_PADDING * 2;
  const groupHeight = maxY - minY + GROUP_PADDING * 2 + 20;

  const groupId = uuidv4();

  const groupNode = createGroupReactFlowNode({
    id: groupId,
    position: { x: groupX, y: groupY },
    size: { width: groupWidth, height: groupHeight },
  }) as unknown as NoxusReactflowNodeV2;

  const nodeIdsToGroup = new Set(nodesToGroup.map((n) => n.id));

  const updatedNodes = nodes
    .filter((n) => !dissolvedGroupIds.has(n.id))
    .map((n) => {
      const absChild = childrenOfDissolved.find((c) => c.id === n.id);
      if (absChild) {
        return {
          ...absChild,
          parentId: groupId,
          extent: "parent" as const,
          position: {
            x: absChild.position.x - groupX,
            y: absChild.position.y - groupY,
          },
          selected: false,
        };
      }
      if (nodeIdsToGroup.has(n.id)) {
        return {
          ...n,
          parentId: groupId,
          extent: "parent" as const,
          position: {
            x: n.position.x - groupX,
            y: n.position.y - groupY,
          },
          selected: false,
        };
      }
      return n;
    });

  const nonChildNodes = updatedNodes.filter((n) => !nodeIdsToGroup.has(n.id));
  const childNodes = updatedNodes.filter((n) => nodeIdsToGroup.has(n.id));
  const nextNodes = [...nonChildNodes, groupNode, ...childNodes];

  recordTransaction(
    get,
    set,
    `Group ${nodesToGroup.length} nodes`,
    ({ nodes, edges }) => {
      nodes.length = 0;
      for (const n of nextNodes) {
        nodes.push(n as unknown as (typeof nodes)[number]);
      }
      removeEdgesTouching(edges, dissolvedGroupIds);
    }
  );
  markDirty(set);

  return groupId;
});

/** A group renders no handles, so an edge on one is an authoring leftover;
 *  drop it with the group instead of leaving a dangling reference behind. */
function removeEdgesTouching(edges: Edge[], nodeIds: Set<string>): void {
  for (let i = edges.length - 1; i >= 0; i--) {
    if (nodeIds.has(edges[i].source) || nodeIds.has(edges[i].target)) {
      edges.splice(i, 1);
    }
  }
}

// ── Ungroup ───────────────────────────────────────────────────────────────

export const ungroupNodesV2Atom = atom(
  null,
  (get, set, args: { groupId: string }) => {
    if (get(isEditorReadOnlyAtom)) return;
    const { groupId } = args;
    const nodes = get(nodesAtom);
    const groupNode = nodes.find((n) => n.id === groupId);
    if (!groupNode) return;

    const updatedNodes = nodes
      .filter((n) => n.id !== groupId)
      .map((n) => {
        if (n.parentId === groupId) {
          return {
            ...n,
            parentId: undefined,
            extent: undefined as "parent" | undefined,
            position: {
              x: n.position.x + groupNode.position.x,
              y: n.position.y + groupNode.position.y,
            },
          };
        }
        return n;
      });

    recordTransaction(get, set, "Ungroup", ({ nodes, edges }) => {
      nodes.length = 0;
      for (const n of updatedNodes) {
        nodes.push(n as unknown as (typeof nodes)[number]);
      }
      removeEdgesTouching(edges, new Set([groupId]));
    });
    markDirty(set);
  }
);

// ── Remove nodes from their group ─────────────────────────────────────────

export const removeNodesFromGroupV2Atom = atom(
  null,
  (get, set, args: { nodeIds: string[] }) => {
    if (get(isEditorReadOnlyAtom)) return;
    const nodes = get(nodesAtom);
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const leaving = args.nodeIds.filter((id) => {
      const node = byId.get(id);
      return !!node?.parentId && isGroupReactFlowNode(byId.get(node.parentId));
    });
    if (leaving.length === 0) return;

    const label =
      leaving.length === 1
        ? `Remove ${findNodeDisplayName(nodes, leaving[0])} from group`
        : `Remove ${leaving.length} nodes from group`;
    const leavingIds = new Set(leaving);
    recordTransaction(get, set, label, ({ nodes: draft }) => {
      for (const n of draft) {
        const original = leavingIds.has(n.id) ? byId.get(n.id) : undefined;
        if (!original) continue;
        n.position = absoluteNodePosition(original, byId);
        n.parentId = undefined;
        n.extent = undefined;
      }
    });
    markDirty(set);
  }
);

// ── Convert selection to subflow ──────────────────────────────────────────

export const convertSelectionToSubflowV2Atom = atom(
  null,
  (
    _get,
    _set,
    _args: {
      spotGroupId: string;
      nodeRegistry: NodeDefinition[];
    }
  ) => {
    // TODO(editor-v2): Re-implement "Convert to subflow" for the V2 binding
    // model. The previous edge-based port was removed because it doesn't match
    // how V2 wires data: V2 flows pass data through variable refs
    // (`:var[<nodeId>.<output>]` in node configs + `variableBindings`), NOT
    // through edges (edges are cosmetic). The correct algorithm operates on
    // BOUND VARIABLES, not boundary edges:
    //
    //   inside = the converted node set.
    //   Scan each inside node's nodeConfig (recursively, incl. rich-text
    //   `((ref))` mentions) + variableBindings for `:var[<id>.<out>]` refs
    //   (REF_TOKEN_RE = /:var\[([^\]]+)\]/g; split on "." -> id + output).
    //
    //   INPUTS (refs pointing INTO the group):
    //     - Distinct refs `:var[ext.out]` used by an inside node where `ext`
    //       is OUTSIDE the group (skip `__secrets__`).
    //     - One subflow Input node per distinct external ref (typed from the
    //       ref's data type).
    //     - Inside the subflow, rewrite `:var[ext.out]` ->
    //       `:var[inputNodeId.output]` in every inside node's config+bindings.
    //     - On the InvokeSubflowNode, bind input-row <label> -> the original
    //       `ext.out` (variableBindings["inputs__<label>"]).
    //
    //   OUTPUTS (refs pointing OUT of the group):
    //     - Distinct refs `:var[inside.out]` used by an OUTSIDE node.
    //     - One subflow Output node per distinct internal ref, bound to
    //       `:var[insideNode.out]`.
    //     - In the parent, rewrite every outside node's `:var[inside.out]` ->
    //       `:var[invokeNodeId.<outputLabel>]`.
    //
    //   Edges are cosmetic and re-derived from the bindings.
    //
    //   Plumbing still to build:
    //     - The ref remapper exists: `remapVarRefsInValue` /
    //       `remapVariableBindingRefs` in SpotflowV2/Sidebar/variableRefs.ts
    //       (used by `remapNodeIds` for paste) — reuse it here with a
    //       full-ref mapper (`ext.out` -> `inputNode.output`). It covers
    //       `:var[...]` tokens + bindings, not rich-text `((ref))` mentions.
    //     - Create the subflow via WorkflowService.addWorkflow, then
    //       `await NodesService.getNodeConfig({ node_id: "InvokeSubflowNode",
    //       workflow_id })` to seed the invoke node's input keys + output
    //       handles before wiring bindings.
    //     - Build subflow Input/Output nodes with proper `{ label, type }`
    //       config (the old port left them empty).
    //     - Fix the GroupNodeV2 "Convert to subflow" button: it passes
    //       `nodeRegistry: []`.
    //
    // Parked as a no-op (rather than the half-built edge-based path, which
    // created orphan subflow workflows and a disconnected invoke node).
    SpotToast.info("Convert to subflow isn't available in the V2 editor yet.");
  }
);

function applyLayoutToNode(node: NoxusReactflowNodeV2, layouted: Node): void {
  node.position = layouted.position;
  if (layouted.width !== undefined) node.width = layouted.width;
  if (layouted.height !== undefined) node.height = layouted.height;
  if (layouted.style !== undefined) {
    node.style = layouted.style as NoxusReactflowNodeV2["style"];
  }
}

export const autoArrangeAtom = atom(
  null,
  async (get, set, origin: DirtyOrigin = "user"): Promise<boolean> => {
    if (get(isEditorReadOnlyAtom)) return false;

    const currentNodes = get(nodesAtom);
    const currentEdges = get(edgesAtom);
    if (currentNodes.length === 0) return false;

    let layoutedNodes: Node[];
    try {
      layoutedNodes = await applyDagreLayout(
        currentNodes,
        currentEdges,
        get(nodeRegistryAtom)
      );
    } catch (err) {
      console.error("Error applying auto-layout:", err);
      return false;
    }

    const layoutedById = new Map(layoutedNodes.map((n) => [n.id, n]));

    recordTransaction(get, set, "Auto-arrange", ({ nodes }) => {
      for (const node of nodes) {
        const layouted = layoutedById.get(node.id);
        if (layouted) applyLayoutToNode(node, layouted);
      }
    });
    markDirty(set, origin);
    return true;
  }
);
